import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Download, UploadCloud, ShieldCheck, FileText, PauseCircle } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import StepIndicator from '../../components/ui/StepIndicator';
import StatusChip from '../../components/ui/StatusChip';
import WorkflowTimeline from '../../components/ui/WorkflowTimeline';
import { useAuth } from '../../context/AuthContext';

const STEP_LABELS = ['Initiated', 'QC Inspection', 'Dispatched', 'Security Outward', 'Resource Verified', 'Released'];
const STATUS_TO_STEP = {
  initiated: 0, qc_pending: 1, repacking: 1, inspection_failed: 1,
  dispatched: 2, security_out: 3, resource_verified: 4, on_hold: 4, released: 5,
};

// Backend serves uploaded files from its origin, not the /api-prefixed base URL.
const API_ORIGIN = api.defaults.baseURL.replace(/\/api\/?$/, '');
const fileUrl = (path) => (path?.startsWith('/') ? `${API_ORIGIN}${path}` : path);

const DispatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [qcForm, setQcForm]     = useState({ sampleSize: '', notes: '' });
  const [qcItems, setQcItems]   = useState([]);
  const [repackNotes, setRepackNotes] = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [secOut, setSecOut] = useState({
    outwardNo: '', customerInvoiceNumber: '', customerInvoiceDate: '',
    poNumber: '', totalQuantity: '', vehicleNumber: '', docketLRNumber: '', transporterName: '',
  });
  const [secSubmitting, setSecSubmitting] = useState(false);
  const [slipLoading, setSlipLoading] = useState(false);
  const [outwardFile, setOutwardFile] = useState(null);
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [resourceNote, setResourceNote] = useState('');
  const [resourceSubmitting, setResourceSubmitting] = useState(false);
  const [gateNote, setGateNote] = useState('');
  const [gateSubmitting, setGateSubmitting] = useState(false);

  const fetchDispatch = () => {
    api.get(`/dispatch/${id}`)
      .then((r) => {
        setDispatch(r.data.data);
        setQcItems(
          (r.data.data.items || []).map((it) => ({
            itemName: it.itemName,
            quantity: it.quantity,
            unit: it.unit,
            acceptedQty: it.quantity,
            rejectedQty: 0,
            remark: '',
          }))
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDispatch(); }, [id]);

  const updateQcItem = (i, field, value) => {
    setQcItems((prev) => prev.map((it, idx) => {
      if (idx !== i) return it;
      if (field === 'acceptedQty') {
        const accepted = Math.max(0, Math.min(it.quantity, Number(value) || 0));
        return { ...it, acceptedQty: accepted, rejectedQty: it.quantity - accepted };
      }
      return { ...it, [field]: value };
    }));
  };

  const handleQC = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch(`/dispatch/${id}/qc`, {
        sampleSize: Number(qcForm.sampleSize),
        notes: qcForm.notes,
        items: qcItems.map((it) => ({
          itemName: it.itemName,
          acceptedQty: it.acceptedQty,
          rejectedQty: it.rejectedQty,
          remark: it.remark,
        })),
      });
      toast.success('QC inspection recorded');
      fetchDispatch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSecurityOut = async (e) => {
    e.preventDefault();
    setSecSubmitting(true);
    try {
      await api.patch(`/dispatch/${id}/security-out`, {
        ...secOut,
        totalQuantity: Number(secOut.totalQuantity) || 0,
      });
      toast.success('Security outward submitted — pending Resource Security');
      fetchDispatch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record security outward');
    } finally {
      setSecSubmitting(false);
    }
  };

  const handleRepack = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.patch(`/dispatch/${id}/repack`, { notes: repackNotes });
      toast.success('Repacking confirmed. Dispatch finalised!');
      fetchDispatch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadSlip = async () => {
    setSlipLoading(true);
    try {
      const res = await api.get(`/dispatch/${id}/slip`);
      const { dispatch: slipDispatch, pickPlan } = res.data.data;

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Dispatch Slip', 14, 18);
      doc.setFontSize(10);
      doc.text(`Dispatch No: ${slipDispatch.dispatchNumber}`, 14, 27);
      doc.text(
        `Date: ${slipDispatch.dispatchDate ? format(new Date(slipDispatch.dispatchDate), 'dd MMM yyyy HH:mm') : format(new Date(), 'dd MMM yyyy HH:mm')}`,
        14, 33
      );
      doc.text(`Client: ${slipDispatch.client?.name || '—'}`, 14, 39);
      doc.text(`Consignee: ${slipDispatch.consignee || '—'}`, 14, 45);

      const body = [];
      (pickPlan || []).forEach((item) => {
        const pickFrom = (item.locations || [])
          .map((l) => `${l.rack} / ${l.floor} / ${l.qty}`)
          .join('; ') || '—';
        body.push([item.itemName, `${item.quantity} ${item.unit || ''}`.trim(), pickFrom]);
        if (item.shortBy > 0) {
          body.push([{
            content: `SHORT by ${item.shortBy}`,
            colSpan: 3,
            styles: { textColor: [232, 25, 44], fontStyle: 'bold' },
          }]);
        }
      });

      autoTable(doc, {
        startY: 52,
        head: [['Item', 'Qty', 'Pick from (Rack / Floor / Qty)']],
        body,
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [232, 25, 44] },
      });

      doc.save(`dispatch-slip-${slipDispatch.dispatchNumber}.pdf`);
      toast.success('Dispatch slip downloaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate dispatch slip');
    } finally {
      setSlipLoading(false);
    }
  };

  const handleUploadOutward = async (e) => {
    e.preventDefault();
    if (!outwardFile) return toast.error('Choose a file first');
    const formData = new FormData();
    formData.append('file', outwardFile);
    setUploadSubmitting(true);
    try {
      await api.post(`/dispatch/${id}/outward-attachment`, formData);
      toast.success('Outward document uploaded');
      setOutwardFile(null);
      fetchDispatch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadSubmitting(false);
    }
  };

  const handleResourceVerify = async () => {
    setResourceSubmitting(true);
    try {
      await api.patch(`/dispatch/${id}/resource-verify`, { note: resourceNote });
      toast.success('Outward document verified — pending Final Security Release');
      setResourceNote('');
      fetchDispatch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setResourceSubmitting(false);
    }
  };

  const handleGateDecision = async (action) => {
    if (action === 'hold' && !gateNote.trim()) {
      return toast.error('A reason is required to place this dispatch on hold');
    }
    setGateSubmitting(true);
    try {
      await api.patch(`/dispatch/${id}/gate-verify`, { action, note: gateNote });
      toast.success(action === 'release' ? 'Dispatch verified & released at gate' : 'Dispatch placed on hold');
      setGateNote('');
      fetchDispatch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setGateSubmitting(false);
    }
  };

  if (loading) return (
    <div className="space-y-4">
      {[...Array(2)].map((_, i) => <div key={i} className="zip-card h-40 skeleton" />)}
    </div>
  );
  if (!dispatch) return <div className="text-center text-zip-muted py-24">Dispatch not found</div>;

  const currentStep = STATUS_TO_STEP[dispatch.status] ?? 0;
  const totalQty = dispatch.items.reduce((s, i) => s + i.quantity, 0);
  const suggestedSample = Math.max(1, Math.ceil(totalQty * 0.1));
  const hasQcResult = !!dispatch.sampleInspection?.result;

  const stage = (done, current) => (done ? 'done' : current ? 'current' : 'pending');
  const dispatchedDone = ['dispatched', 'security_out', 'resource_verified', 'on_hold', 'released'].includes(dispatch.status);
  const securityOutDone = ['security_out', 'resource_verified', 'on_hold', 'released'].includes(dispatch.status);
  const resourceVerifiedDone = ['resource_verified', 'released'].includes(dispatch.status) || (dispatch.status === 'on_hold' && dispatch.resourceVerify?.verified);
  const releasedDone = dispatch.status === 'released';
  const workflowSteps = [
    { label: 'Dispatch Created', state: 'done' },
    { label: hasQcResult ? `Dispatch QC ${dispatch.qcStatus === 'failed' ? '— Rejected' : dispatch.qcStatus === 'partial' ? '— Partially Passed' : '— Passed'}` : 'Dispatch QC', state: dispatch.qcStatus === 'failed' ? 'rejected' : stage(hasQcResult, !hasQcResult) },
    { label: 'Security Outward', state: stage(securityOutDone, dispatchedDone && !securityOutDone) },
    { label: 'Resource Security Verified', state: stage(resourceVerifiedDone, securityOutDone && !resourceVerifiedDone) },
    { label: dispatch.status === 'on_hold' ? 'Final Security — On Hold' : 'Final Security Release', state: dispatch.status === 'on_hold' ? 'rejected' : stage(releasedDone, resourceVerifiedDone && !releasedDone) },
  ];

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/dispatch')}
          className="flex items-center gap-2 text-sm text-zip-muted hover:text-zip-text transition-colors"
        >
          <ArrowLeft size={15} /> Back to Dispatch
        </button>
        {can('dispatch.view') && (
          <button
            type="button"
            onClick={handleDownloadSlip}
            disabled={slipLoading}
            className="btn-ghost flex items-center gap-2 text-sm"
          >
            <Download size={15} /> {slipLoading ? 'Preparing…' : 'Download Dispatch Slip'}
          </button>
        )}
      </div>

      <div className="zip-card px-5 py-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-zip-red text-sm font-medium">{dispatch.dispatchNumber}</span>
            <StatusChip status={dispatch.status} />
          </div>
          <span className="text-zip-muted text-xs font-mono">
            {dispatch.client?.name ?? '—'}{dispatch.consignee ? ` → ${dispatch.consignee}` : ''}
          </span>
        </div>
        <StepIndicator steps={STEP_LABELS} current={currentStep} />
      </div>

      <WorkflowTimeline steps={workflowSteps} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Items */}
        <div className="zip-card overflow-hidden">
          <div className="px-5 py-4 border-b border-zip-border">
            <h3 className="font-semibold text-zip-text tracking-tight">Dispatch Items</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-zip-surface border-b border-zip-border">
                {[
                  'Item', 'Qty', 'Unit', 'Floor',
                  ...(hasQcResult ? ['Accepted', 'Rejected'] : []),
                ].map((h) => <th key={h} className="zip-th">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {dispatch.items.map((item, i) => (
                <tr key={i} className="border-b border-zip-border last:border-0">
                  <td className="zip-td">{item.itemName}</td>
                  <td className="zip-td font-mono">{item.quantity}</td>
                  <td className="zip-td font-mono text-zip-muted">{item.unit || 'pcs'}</td>
                  <td className="zip-td">
                    <span className="chip-blue">{item.floor || '—'}</span>
                  </td>
                  {hasQcResult && (
                    <>
                      <td className="zip-td font-mono text-emerald-600">{item.acceptedQty ?? item.quantity}</td>
                      <td className="zip-td font-mono text-zip-red">{item.rejectedQty || 0}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 text-xs text-zip-muted font-mono border-t border-zip-border">
            Total Qty: <span className="text-zip-text font-semibold">{totalQty}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          {dispatch.status === 'initiated' && (
            <div className="zip-card p-5">
              <h3 className="font-semibold text-zip-text tracking-tight mb-4">Dispatch QC Inspection</h3>
              <form onSubmit={handleQC} className="space-y-3">
                <div>
                  <label className="block text-xs text-zip-muted mb-1.5">
                    Sample Size <span className="text-zip-muted/60">(suggested: {suggestedSample})</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="zip-input"
                    required
                    value={qcForm.sampleSize}
                    onChange={(e) => setQcForm({ ...qcForm, sampleSize: e.target.value })}
                    placeholder={suggestedSample.toString()}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs text-zip-muted">Per-item Accepted / Rejected Qty</label>
                  {qcItems.map((it, i) => (
                    <div key={i} className="border border-zip-border rounded-lg p-3 space-y-2 bg-zip-surface">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-zip-text">{it.itemName}</span>
                        <span className="font-mono text-xs text-zip-muted">Picked: {it.quantity} {it.unit || 'pcs'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] text-zip-muted mb-1">Accepted Qty</label>
                          <input
                            type="number"
                            min="0"
                            max={it.quantity}
                            className="zip-input text-sm"
                            value={it.acceptedQty}
                            onChange={(e) => updateQcItem(i, 'acceptedQty', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] text-zip-muted mb-1">Rejected Qty</label>
                          <input
                            type="number"
                            className="zip-input text-sm opacity-60 cursor-not-allowed"
                            value={it.rejectedQty}
                            readOnly
                          />
                        </div>
                      </div>
                      {it.rejectedQty > 0 && (
                        <input
                          type="text"
                          className="zip-input text-xs"
                          placeholder="Reason for rejection (goes to Dispatch Hold)…"
                          value={it.remark}
                          onChange={(e) => updateQcItem(i, 'remark', e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <textarea
                  className="zip-input resize-none"
                  rows={2}
                  placeholder="Overall QC notes…"
                  value={qcForm.notes}
                  onChange={(e) => setQcForm({ ...qcForm, notes: e.target.value })}
                />
                <button type="submit" disabled={submitting} className="btn-primary w-full">
                  {submitting ? 'Submitting…' : 'Submit QC'}
                </button>
              </form>
            </div>
          )}

          {dispatch.status === 'repacking' && (
            <div className="zip-card p-5">
              <h3 className="font-semibold text-zip-text tracking-tight mb-3">Repacking</h3>
              <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2.5 text-xs mb-4">
                QC failed. Complete repacking/relabeling before dispatch.
              </div>
              <form onSubmit={handleRepack} className="space-y-3">
                <textarea
                  className="zip-input resize-none"
                  rows={3}
                  placeholder="Repacking notes…"
                  value={repackNotes}
                  onChange={(e) => setRepackNotes(e.target.value)}
                />
                <button type="submit" disabled={submitting} className="btn-primary w-full">
                  {submitting ? 'Confirming…' : 'Confirm Repacking Done'}
                </button>
              </form>
            </div>
          )}

          {dispatch.status === 'inspection_failed' && (
            <div className="zip-card p-5 flex flex-col items-center justify-center gap-2 text-center py-10 border-l-4 border-l-amber-500"
              style={{ borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.05)' }}
            >
              <StatusChip status="inspection_failed" />
              <div className="text-amber-600 font-semibold mt-1">QC inspection failed</div>
              <div className="text-zip-muted text-xs">Needs review before it can move forward.</div>
            </div>
          )}

          {dispatch.status === 'on_hold' && (
            <div className="zip-card p-5 flex flex-col items-center justify-center gap-2 text-center py-10 border-l-4 border-l-red-500"
              style={{ borderColor: 'rgba(232,25,44,0.25)', background: 'rgba(232,25,44,0.05)' }}
            >
              <PauseCircle size={28} className="text-zip-red" />
              <div className="text-zip-red font-semibold mt-1">On Hold at Final Security</div>
              {dispatch.gateHold?.reason && <div className="text-zip-muted text-xs">{dispatch.gateHold.reason}</div>}
            </div>
          )}

          {dispatch.status === 'released' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="zip-card p-5 flex flex-col items-center justify-center gap-2 text-center py-10"
              style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.05)' }}
            >
              <ShieldCheck size={32} className="text-emerald-600" />
              <div className="text-emerald-600 font-semibold">Released / Exited</div>
              <div className="text-zip-muted text-xs font-mono">
                {dispatch.gateVerify?.verifiedAt ? format(new Date(dispatch.gateVerify.verifiedAt), 'dd MMM yyyy HH:mm') : '—'}
              </div>
            </motion.div>
          )}

          {hasQcResult && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">QC Record</h3>
              {[
                ['Result', dispatch.sampleInspection.result === 'pass'
                  ? <span className="text-emerald-600 font-bold font-mono">PASS</span>
                  : dispatch.sampleInspection.result === 'partial'
                  ? <span className="text-amber-600 font-bold font-mono">PARTIAL</span>
                  : <span className="text-zip-red font-bold font-mono">FAIL</span>
                ],
                ['Sample Size', <span className="font-mono">{dispatch.sampleInspection.sampleSize}</span>],
                dispatch.sampleInspection.notes && ['Notes', dispatch.sampleInspection.notes],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-zip-muted">{k}</span>
                  <span className="text-zip-text">{v}</span>
                </div>
              ))}
              {dispatch.sampleInspection.result === 'partial' && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Rejected quantities were sent to Dispatch Hold for review.
                </p>
              )}
            </div>
          )}

          {dispatch.status === 'dispatched' && !can('dispatch.security_out') && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="zip-card p-5 flex flex-col items-center justify-center gap-2 text-center py-10"
              style={{ borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.05)' }}
            >
              <CheckCircle size={32} className="text-emerald-600" />
              <div className="text-emerald-600 font-semibold">Dispatched</div>
              <div className="text-zip-muted text-xs font-mono">
                {dispatch.dispatchDate ? format(new Date(dispatch.dispatchDate), 'dd MMM yyyy HH:mm') : '—'}
              </div>
              <div className="text-zip-muted text-xs">Awaiting Security Outward entry.</div>
            </motion.div>
          )}

          {/* Security Outward */}
          {dispatch.status === 'dispatched' && can('dispatch.security_out') && (
            <div className="zip-card p-5 space-y-4">
              <h3 className="font-semibold text-zip-text tracking-tight">Security Outward Gate</h3>
              <form onSubmit={handleSecurityOut} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['outwardNo',             'Outward No. (manual)', 'OUT-001', 'text'],
                    ['customerInvoiceNumber', 'Customer Invoice No.', 'CINV-001', 'text'],
                    ['customerInvoiceDate',   'Invoice Date',         '',         'date'],
                    ['poNumber',              'PO No.',               'PO-001',   'text'],
                    ['totalQuantity',         'Total Quantity',        '0',        'number'],
                    ['vehicleNumber',         'Vehicle No.',           'MH-12-AB-1234', 'text'],
                    ['docketLRNumber',        'Docket / LR No.',      'LR-001',   'text'],
                    ['transporterName',       'Transporter Name',     'ABC Logistics', 'text'],
                  ].map(([field, label, ph, type]) => (
                    <div key={field}>
                      <label className="block text-xs text-zip-muted mb-1">{label}</label>
                      <input type={type} min={type === 'number' ? '0' : undefined}
                        className="zip-input text-sm" placeholder={ph}
                        value={secOut[field]}
                        onChange={(e) => setSecOut({ ...secOut, [field]: e.target.value })} />
                    </div>
                  ))}
                </div>
                <button type="submit" disabled={secSubmitting} className="btn-primary w-full">
                  {secSubmitting ? 'Recording…' : 'Record Security Outward'}
                </button>
              </form>
            </div>
          )}

          {['security_out', 'resource_verified', 'on_hold', 'released'].includes(dispatch.status) && dispatch.securityOut?.recordedAt && (
            <div className="zip-card p-5 space-y-3 border-l-4 border-l-emerald-500">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">Security Outward</h3>
              {[
                dispatch.securityOut.outwardNo             && ['Outward No.',        dispatch.securityOut.outwardNo],
                dispatch.securityOut.customerInvoiceNumber && ['Customer Invoice No.',dispatch.securityOut.customerInvoiceNumber],
                dispatch.securityOut.customerInvoiceDate   && ['Invoice Date',        format(new Date(dispatch.securityOut.customerInvoiceDate), 'dd MMM yyyy')],
                dispatch.securityOut.poNumber              && ['PO No.',              dispatch.securityOut.poNumber],
                dispatch.securityOut.totalQuantity         && ['Total Qty',           dispatch.securityOut.totalQuantity],
                dispatch.securityOut.vehicleNumber         && ['Vehicle No.',         dispatch.securityOut.vehicleNumber],
                dispatch.securityOut.docketLRNumber        && ['Docket / LR No.',     dispatch.securityOut.docketLRNumber],
                dispatch.securityOut.transporterName       && ['Transporter',         dispatch.securityOut.transporterName],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-zip-muted">{k}</span>
                  <span className="text-zip-text font-mono text-xs">{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Outward document upload — Resource Security */}
          {['security_out', 'resource_verified', 'on_hold', 'released'].includes(dispatch.status) && can('dispatch.resource_verify') && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight">Outward Document</h3>
              {dispatch.outwardAttachment && (
                <a
                  href={fileUrl(dispatch.outwardAttachment)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-zip-red hover:underline"
                >
                  <FileText size={15} /> View uploaded outward document
                </a>
              )}
              {dispatch.status === 'security_out' && (
                <form onSubmit={handleUploadOutward} className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => setOutwardFile(e.target.files?.[0] || null)}
                    className="zip-input text-xs flex-1"
                  />
                  <button type="submit" disabled={uploadSubmitting} className="btn-primary flex items-center gap-2 whitespace-nowrap">
                    <UploadCloud size={15} /> {uploadSubmitting ? 'Uploading…' : 'Upload'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Resource Security verification */}
          {dispatch.status === 'security_out' && can('dispatch.resource_verify') && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight">Resource Security — Verify Outward Document</h3>
              <p className="text-xs text-zip-muted">
                Confirm the Security Outward entry and/or uploaded document are correct before this moves to Final Security Release.
              </p>
              <textarea
                className="zip-input resize-none"
                rows={2}
                placeholder="Verification note (optional)…"
                value={resourceNote}
                onChange={(e) => setResourceNote(e.target.value)}
              />
              <button
                type="button"
                onClick={handleResourceVerify}
                disabled={resourceSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <ShieldCheck size={15} /> {resourceSubmitting ? 'Verifying…' : 'Verify Document'}
              </button>
            </div>
          )}

          {dispatch.resourceVerify?.verified && (
            <div className="zip-card p-5 space-y-3 border-l-4 border-l-emerald-500">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">Resource Security Verification</h3>
              {[
                ['Verified By', dispatch.resourceVerify.verifiedBy?.name || dispatch.resourceVerify.verifiedBy],
                ['Verified At', dispatch.resourceVerify.verifiedAt ? format(new Date(dispatch.resourceVerify.verifiedAt), 'dd MMM yyyy HH:mm') : '—'],
                dispatch.resourceVerify.note && ['Note', dispatch.resourceVerify.note],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-zip-muted">{k}</span>
                  <span className="text-zip-text text-xs">{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Final Security release / hold */}
          {['resource_verified', 'on_hold'].includes(dispatch.status) && can('dispatch.gateout') && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight">Final Security — Release or Hold</h3>
              <textarea
                className="zip-input resize-none"
                rows={2}
                placeholder="Note (required if placing on hold)…"
                value={gateNote}
                onChange={(e) => setGateNote(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleGateDecision('release')}
                  disabled={gateSubmitting}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <ShieldCheck size={15} /> {gateSubmitting ? 'Working…' : 'Release'}
                </button>
                <button
                  type="button"
                  onClick={() => handleGateDecision('hold')}
                  disabled={gateSubmitting}
                  className="flex items-center justify-center gap-2 rounded-lg border border-zip-red text-zip-red font-semibold text-sm py-2 hover:bg-zip-red/5 transition-colors disabled:opacity-50"
                >
                  <PauseCircle size={15} /> Hold
                </button>
              </div>
            </div>
          )}

          {dispatch.status === 'released' && dispatch.gateVerify?.verified && (
            <div className="zip-card p-5 space-y-3 border-l-4 border-l-emerald-500">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">Gate Verification</h3>
              {[
                ['Verified By', dispatch.gateVerify.verifiedBy?.name || dispatch.gateVerify.verifiedBy],
                ['Verified At', dispatch.gateVerify.verifiedAt ? format(new Date(dispatch.gateVerify.verifiedAt), 'dd MMM yyyy HH:mm') : '—'],
                dispatch.gateVerify.note && ['Note', dispatch.gateVerify.note],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-zip-muted">{k}</span>
                  <span className="text-zip-text text-xs">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invoice / part / batch detail per item */}
      <div className="zip-card overflow-hidden">
        <div className="px-5 py-4 border-b border-zip-border">
          <h3 className="font-semibold text-zip-text tracking-tight">Invoice &amp; Part Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zip-surface border-b border-zip-border">
                {[
                  'Item Name / Part Description', 'Invoice No', 'Invoice Date', 'Part Number',
                  'Part Rev.', 'Box/Coil Qty', 'Invoice Qty', 'Batch No',
                ].map((h) => <th key={h} className="zip-th whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {dispatch.items.map((item, i) => (
                <tr key={i} className="border-b border-zip-border last:border-0">
                  {/* Item Name and Part Description hold the same value — shown once. */}
                  <td className="zip-td">{item.itemName || item.partDescription || '—'}</td>
                  <td className="zip-td font-mono text-xs">{item.invoiceNo || '—'}</td>
                  <td className="zip-td font-mono text-xs whitespace-nowrap">
                    {item.invoiceDate ? format(new Date(item.invoiceDate), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="zip-td font-mono text-xs">{item.partNumber || '—'}</td>
                  <td className="zip-td font-mono text-xs">{item.partRev || '—'}</td>
                  <td className="zip-td font-mono text-xs">{item.boxQty ?? '—'}</td>
                  <td className="zip-td font-mono text-xs">{item.invoiceQty ?? '—'}</td>
                  <td className="zip-td font-mono text-xs">{item.batchNo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DispatchDetail;
